//@flow
/**
 * Collection of learner utility functions
 * @module learner/lib
 */
import qs from 'qs'
import uuid from 'uuid'
import { TextEncoder } from 'text-encoding'
import { encode as encodeToBase64 } from 'base64-arraybuffer'
import { sleep } from '../../utils'
import socket from '../../socket'
import { LearnerRecord } from './reducer'
import { publicKey, allTestLevels } from '../../constants'
import { dpQuestionCategories, dpPercentageComputation, dpQuestions } from '../../constants/dp'

/**
 * Sends a question submission to the server.
 * @memberof learner/lib
 */
export function submitQuestion (submission: QuestionSubmission) {
  return new Promise((resolve, reject) => {
    socket.emit('learner:submit-question', submission, (err, response) => {
      if (err) {
        return reject(err)
      }
      resolve(response)
    })
  })
}

/**
 * Sends a learner form submission to the server.
 * @memberof learner/lib
 */
export function submitLearner (submission: LearnerDetailsSubmission) {
  return new Promise((resolve, reject) => {
    let socketName = 'learner:details';
    if (submission.quizId === 'dp' || submission.quizId === 'dpcourse') {
      socketName = 'learner:details-digital'
    }
    socket.emit(socketName, submission, (err, response) => {
      if (err) {
        return reject(err)
      }
      resolve(response)
    })
  })
}

/**
 * Adds invite to browser location if PushState api available.
 * @memberof learner/lib
 */
export function addInviteToLocation (invite: string) {
  if (window.history.pushState) {
    const searchString = qs.stringify({
        ...qs.parse(window.location.search, {
          ignoreQueryPrefix: true
        }),
        invite
      }),
      path = `${window.location.pathname}?${searchString}`
    window.history.pushState(
      {
        path
      },
      '',
      path
    )
  }
}

/**
 * return a promise that resolves when the learner is registered.
 * @memberof learner/lib
 */
export async function whenLearnerRegistered (pollFreq: number = 1000) {
  let registered = false
  while (true) {
    registered = await new Promise(resolve => {
      socket.emit('status', async status => {
        resolve(status && status.registered)
      })
    })
    if (registered) return
    await sleep(pollFreq)
  }
}

/**
 * returns the proposed quiz id based on url query string ?quizId=...
 * @memberof learner/lib
 */
export function getProposedQuizId () {
  const { quizId: proposedQuizId } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true
  })
  if (!allTestLevels.includes(proposedQuizId)) {
    return 'dp'
  }
  return proposedQuizId
}

/**
 * encrypts all pending submissions, form, and questions.
 * @memberof learner/lib
 */
export async function encryptPendingSubmissions (learnerState: LearnerRecord) {
  if (window.crypto.subtle == null) {
    throw new Error('Webcrypto unavailable.')
  }
  // Construct submissions object
  const form = learnerState.pendingFormSubmission
    ? learnerState.pendingFormSubmission.toJS()
    : null
  const invite = learnerState.invite !== '' ? learnerState.invite : null
  const submissions = {
    // only provide form if invite is not provided
    form: invite == null ? form : null,
    invite,
    answers: learnerState.pendingQuestionSubmissions
      .toJS()
      .map(({ answer }) => ({ ...answer, correct: undefined }))
  }
  if (submissions.form == null && submissions.invite == null) {
    throw new Error(
      'Unable to identify learner. No form submission or invite code provided.'
    )
  }
  const data = stringToArrayBuffer(JSON.stringify(submissions))
  const key = await generateKey()
  const { data: encryptedData, iv } = await encryptData(key, data)
  const encryptedKey = await encryptKey(key)
  const encryptedIv = await encryptPublic(iv)
  const id = uuid.v4()
  const encryptedId = await encryptPublic(stringToArrayBuffer(id))

  return {
    encryptedFileId: id,
    encryptedFile: JSON.stringify({
      id: encryptedId,
      key: encryptedKey,
      iv: encryptedIv,
      data: encryptedData
    })
  }
}

/**
 * wrapper for calls to window.crypto.subtle to handle non-standard ie11 implementation
 * @memberof learner/lib
 */
function subtleToPromise (maybePromise) {
  if (maybePromise != null && typeof maybePromise.then === 'function') {
    return maybePromise
  } else {
    return new Promise((resolve, reject) => {
      const cryptoOperation = maybePromise
      cryptoOperation.oncomplete(resolve)
      cryptoOperation.onerror(reject)
      cryptoOperation.onabort(() =>
        reject(new Error('CryptoOperation aborted.'))
      )
    })
  }
}

/**
 * Encrypts generated key with public key
 * @memberof learner/lib
 */
async function encryptKey (key) {
  const exportedKey = await subtleToPromise(
    window.crypto.subtle.exportKey('jwk', key)
  )
  return encryptPublic(stringToArrayBuffer(JSON.stringify(exportedKey)))
}

/**
 * Encrypts data with public key
 * @memberof learner/lib
 */
async function encryptPublic (data) {
  const importedKey = await getPublicKey()
  const encrypted = await subtleToPromise(
    window.crypto.subtle.encrypt('RSA-OAEP', importedKey, data)
  )
  return encodeToBase64(encrypted)
}

/**
 * Get the public encryption key.
 * @memberof learner/lib
 */
function getPublicKey () {
  return subtleToPromise(
    window.crypto.subtle.importKey(
      'jwk',
      publicKey,
      {
        name: 'RSA-OAEP',
        hash: { name: 'SHA-512' }
      },
      false,
      ['encrypt']
    )
  )
}

/**
 * Converts a string to a ArrayBuffer
 * @memberof learner/lib
 */
function stringToArrayBuffer (str) {
  const encoder = new TextEncoder()
  return encoder.encode(str)
}

/**
 * generates key for data encryption
 * @memberof learner/lib
 */
function generateKey () {
  return subtleToPromise(
    window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 128
      },
      true,
      ['encrypt', 'decrypt']
    )
  )
}

/**
 * encrypts data
 * @memberof learner/lib
 */
async function encryptData (key, data) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const encryptedData = await subtleToPromise(
    window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv
      },
      key,
      data
    )
  )
  return { data: encodeToBase64(encryptedData), iv }
}

/**
 * Sends a question submission to the server.
 * @memberof learner/lib
 */
export function submitDlrqResult (
  result: DlrqResult,
  responses: Array<DlrqResponse>
) {
  return new Promise((resolve, reject) => {
    socket.emit(
      'learner:submit-dlrq-results',
      { result, responses },
      (err, response) => {
        if (err) {
          return reject(err)
        }
        resolve(response)
      }
    )
  })
}

/**
 * Submit Digital Proficiency Response to the server.
 * @memberof learner/lib
 */
export function submitDpResult (
  result,
  responses,
  params
) {
  return new Promise((resolve, reject) => {
    socket.emit(
      'learner:submit-dp-results',
      { result, responses, params },
      (err, response) => {
        if (err) {
          return reject(err)
        }
        resolve(response)
      }
    )
  })
}

/**
 * Unset Digital Proficiency Response from the database
 * @memberof learner/lib
 */
export function unsetDpResult (
  params
) {
  return new Promise((resolve, reject) => {
    socket.emit(
      'learner:unset-dp-results',
      { params },
      (err, response) => {
        if (err) {
          return reject(err)
        }
        resolve(response)
      }
    )
  })
}

/**
 * Returns the quiz title based on the test level
 * @memberof learner/lib
 */
export function getQuizTitle(testLevel = null) {
  testLevel = testLevel || getProposedQuizId()
  const title = {
    'dp': 'Digital Profiler',
    'dpcourse': 'Digital Profiler - Course Aligned',
    'dlrq': 'Course Ready'
  };

  return title[testLevel] || title['dlrq'];
}

/**
 * get current level
 * @returns 
 */
export function getCurrentLevel(dpPrescreeningResult, currentCategory, currentQuestionId = null) {
  let currentLevel = dpPrescreeningResult[currentCategory] || 1
  // check merged categories and use the higher level
  const mergedCategories = {}; //{"3_1": "3_2", "5_2": "5_3"}
  if (typeof mergedCategories[currentCategory] !== 'undefined') {
    const catId2 = mergedCategories[currentCategory]
    if (typeof dpPrescreeningResult[catId2] !== 'undefined'
      && currentLevel < dpPrescreeningResult[catId2]
    ) {
      currentLevel = dpPrescreeningResult[catId2]
    }
  }

  if (currentQuestionId !== null) {
    Object.keys(dpQuestionCategories[currentCategory]).forEach(r => {
      if (dpQuestionCategories[currentCategory][r].indexOf(currentQuestionId) >= 0) {
        currentLevel = r
      }
    })
  }
  return currentLevel;
}

/**
 * get the next question for digital proficiency quiz
 */
export function dpNextQuestionId (
  dpPrescreeningResult,
  currentQuestionId,
  category = '1_1',
  answers = null
) {
  while(true) {
    let skipCategory = false;
    let level = getCurrentLevel(dpPrescreeningResult, category, currentQuestionId)
    let baseLevel = getCurrentLevel(dpPrescreeningResult, category)

    if (answers !== null) {
      const levelQuestionIds = dpQuestionCategories[category][level];
      const totalQuestions = levelQuestionIds.length
      // eslint-disable-next-line no-loop-func

      let categoryAnswer = [], correctAnswers = []
      if (typeof answers[category] !== 'undefined') {
        categoryAnswer = Object.values(answers[category]).filter(ans => {
          return ans.level === level
        }) || []

        correctAnswers = categoryAnswer.filter(answer => answer.correct === true) || []
      }
      const correctPercentage = (correctAnswers.length / totalQuestions) * 100; // current correct answer percentage
      const remainingQuestions = (totalQuestions - categoryAnswer.length)
      const possibleCorrect = (remainingQuestions / totalQuestions) * 100; // remaining question possible correct answer percentage

      const levelIndex = Object.keys(dpQuestionCategories[category])
      // eslint-disable-next-line radix
      const skipPercentage = parseInt(level) === 7 ? 34 :20;
      if (categoryAnswer.length !== totalQuestions && (correctPercentage + possibleCorrect) <= skipPercentage) {
        
        // eslint-disable-next-line radix
        if (parseInt(level) !== 1 && parseInt(level) <= parseInt(baseLevel)) {
          // skip level if correct answer <= 20%
          level = levelIndex[levelIndex.indexOf(level.toString()) - 1]
        } else {
          // skip category if possible correct answer <= 20% and current level == 1
          skipCategory = true;
        }
      } else {
        // get all answered question ids including the linked questions
        let answeredIds = []
        
        if (typeof answers[category] !== 'undefined') {
          Object.values(answers[category]).forEach(a => {
            if (levelQuestionIds.indexOf(a.questionId) !== -1) {
              answeredIds.push(a.questionId)
            }
            if (dpQuestions[a.questionId].questionIdLink !== undefined && levelQuestionIds.indexOf(dpQuestions[a.questionId].questionIdLink) !== -1) {
              answeredIds.push(dpQuestions[a.questionId].questionIdLink)
            }
          })
        }

        // if all questions are answered
        if (answeredIds.length === totalQuestions) {
          // if all level questions are linked to the previous level (category 5.4)
          if (categoryAnswer.length === 0) {
            level = levelIndex[levelIndex.indexOf(level.toString()) + 1]
          } else {
            const scorePercentage = (correctAnswers.length / (categoryAnswer.length)) * 100
            const nextQuestionMap = dpPercentageComputation[baseLevel][level]
          
            // eslint-disable-next-line no-loop-func
            nextQuestionMap.forEach(map => {
              if (scorePercentage >= map.from && scorePercentage <= map.to && map.skip !== undefined) {
                if (map.skip === 'previous') {
                  level = levelIndex[levelIndex.indexOf(level.toString()) - 1]
                } else {
                  level = levelIndex[levelIndex.indexOf(level.toString()) + 1]
                }
              }
            })
          }
        }
      }
    }
  
    // get questions from next category
    const questionGroup = dpQuestionCategories[category][level];
    let nextQuestionId = questionGroup[0] // use the first question id as initial id
    // Initial question Id after prescreening is 0
    if (currentQuestionId !== 0) {
      nextQuestionId = questionGroup[questionGroup.indexOf(currentQuestionId) + 1]
    }
    
    // if next question is undefined, move to next category
    if (nextQuestionId === undefined || skipCategory) {
      const categoryIndex = Object.keys(dpQuestionCategories);
      const currentIndex = categoryIndex.indexOf(category)

      // if no more categories
      if ((currentIndex + 1) === categoryIndex.length) {
        return {
          nextQuestionId: "finish"
        }
      }
      // else, start 1st question from next category
      category = categoryIndex[currentIndex + 1];
      continue;
    }

    // check if question is linked to another question
    const currentQuestion = dpQuestions[nextQuestionId]
    if (answers !== null && currentQuestion.questionIdLink !== undefined && answers[category] !== undefined) {
      const answeredQuestions = Object.keys(answers[category])//answers.map(a => a.questionId)

      if (answeredQuestions.indexOf(currentQuestion.questionIdLink) > -1) {
        currentQuestionId = nextQuestionId
        continue;
      }
    }
    return { nextQuestionId, category, level };
  }
}

/**
 * Normalises a course profile into an array of category score entries.
 */
function normalizeCourseProfile(profile) {
  if (Array.isArray(profile)) {
    return profile
  }
  if (profile !== null && typeof profile === 'object') {
    return Object.keys(profile).map(category => ({
      category,
      score:
        typeof profile[category] === 'object'
          ? profile[category].score
          : profile[category]
    }))
  }
  return []
}

/**
 * Returns the starting and target DP bands for a course score.
 */
function getCourseLevelBounds(score) {
  const numericScore = Number(score)
  if (!Number.isFinite(numericScore) || numericScore <= 0) return null
  if (numericScore <= 2) return { start: '1', target: '1' }
  if (numericScore <= 4) return { start: '1', target: '3' }
  if (numericScore <= 6) return { start: '3', target: '5' }
  return { start: '5', target: '7' }
}

/**
 * Builds a category-to-score map from the fetched course profile.
 */
function getCourseLevelScoreMap(selectedCourseProfile) {
  return normalizeCourseProfile(
    selectedCourseProfile && selectedCourseProfile.profile
  ).reduce((map, item) => {
    const category = item.category ? item.category.replace('.', '_') : null
    const score = Number(item.score)
    if (category && Number.isFinite(score)) {
      map[category] = score
    }
    return map
  }, {})
}

/**
 * Finds the next course-required DP category after the current category.
 */
function getNextCourseCategory(category, scoreMap) {
  const categories = Object.keys(dpQuestionCategories)
  let index = categories.indexOf(category)
  while (index + 1 < categories.length) {
    const nextCategory = categories[index + 1]
    if (getCourseLevelBounds(scoreMap[nextCategory])) {
      return nextCategory
    }
    index += 1
  }
  return null
}

/**
 * Returns the maximum numeric score represented by a DP question band.
 */
function getCourseLevelCapScore(level) {
  return {
    '1': 2,
    '3': 4,
    '5': 6,
    '7': 8
  }[level] || 0
}

/**
 * Converts a bounded skip result into the best score allowed by the course route.
 */
function getCourseLevelFallbackScore(level, skip) {
  const levelIndex = Object.keys(dpPercentageComputation)
  const currentIndex = levelIndex.indexOf(level.toString())
  if (skip === 'next') return getCourseLevelCapScore(level)
  if (skip === 'previous' && currentIndex > 0) {
    return getCourseLevelCapScore(levelIndex[currentIndex - 1])
  }
  return 0
}

/**
 * Builds synthetic DP starting levels from the course profile.
 */
export function dpBuildCoursePrescreeningResult(selectedCourseProfile) {
  const scoreMap = getCourseLevelScoreMap(selectedCourseProfile)
  return Object.keys(dpQuestionCategories).reduce((result, category) => {
    const bounds = getCourseLevelBounds(scoreMap[category])
    if (bounds) result[category] = bounds.start
    return result
  }, {})
}

/**
 * Gets the next bounded course-level DP question.
 */
export function dpNextCourseLevelQuestionId (
  selectedCourseProfile,
  currentQuestionId,
  category = '1_1',
  answers = null
) {
  const scoreMap = getCourseLevelScoreMap(selectedCourseProfile)

  while (true) {
    let skipCategory = false
    const bounds = getCourseLevelBounds(scoreMap[category])

    if (!bounds) {
      const nextCategory = getNextCourseCategory(category, scoreMap)
      if (nextCategory === null) return { nextQuestionId: 'finish' }
      category = nextCategory
      currentQuestionId = 0
      continue
    }

    let level = getCurrentLevel(
      { [category]: bounds.start },
      category,
      currentQuestionId
    ).toString()
    const levelIndex = Object.keys(dpQuestionCategories[category])

    if (levelIndex.indexOf(level) === -1) {
      level = bounds.start
    }

    if (parseInt(level, 10) > parseInt(bounds.target, 10)) {
      skipCategory = true
    }

    if (answers !== null && skipCategory === false) {
      const levelQuestionIds = dpQuestionCategories[category][level]
      const totalQuestions = levelQuestionIds.length
      let categoryAnswer = []
      let correctAnswers = []

      if (typeof answers[category] !== 'undefined') {
        categoryAnswer = Object.values(answers[category]).filter(ans => {
          return ans.level.toString() === level
        }) || []
        correctAnswers = categoryAnswer.filter(answer => answer.correct === true) || []
      }

      const correctPercentage = (correctAnswers.length / totalQuestions) * 100
      const remainingQuestions = totalQuestions - categoryAnswer.length
      const possibleCorrect = (remainingQuestions / totalQuestions) * 100
      const skipPercentage = parseInt(level, 10) === 7 ? 34 : 20
      // Toggle this to true to restore the old course-aligned behavior:
      // skip the rest of a level/category once the learner can no longer
      // exceed the low-score threshold, even before all questions are answered.
      const skipLowPossibleScoreBeforeLevelComplete = false

      if (
        skipLowPossibleScoreBeforeLevelComplete &&
        categoryAnswer.length !== totalQuestions &&
        correctPercentage + possibleCorrect <= skipPercentage
      ) {
        const previousLevel = levelIndex[levelIndex.indexOf(level) - 1]
        if (
          previousLevel &&
          parseInt(level, 10) < parseInt(bounds.target, 10)
        ) {
          level = previousLevel
          currentQuestionId = 0
        } else {
          skipCategory = true
        }
      } else {
        let answeredIds = []

        if (typeof answers[category] !== 'undefined') {
          Object.values(answers[category]).forEach(a => {
            if (levelQuestionIds.indexOf(a.questionId) !== -1) {
              answeredIds.push(a.questionId)
            }
            if (
              dpQuestions[a.questionId].questionIdLink !== undefined &&
              levelQuestionIds.indexOf(dpQuestions[a.questionId].questionIdLink) !== -1
            ) {
              answeredIds.push(dpQuestions[a.questionId].questionIdLink)
            }
          })
        }

        if (answeredIds.length === totalQuestions) {
          if (categoryAnswer.length === 0) {
            const nextLevel = levelIndex[levelIndex.indexOf(level) + 1]
            if (
              nextLevel &&
              parseInt(nextLevel, 10) <= parseInt(bounds.target, 10)
            ) {
              level = nextLevel
              currentQuestionId = 0
            } else {
              skipCategory = true
            }
          } else {
            const scorePercentage =
              (correctAnswers.length / categoryAnswer.length) * 100
            const nextQuestionMap = dpPercentageComputation[bounds.start][level]

            nextQuestionMap.forEach(map => {
              if (
                scorePercentage >= map.from &&
                scorePercentage <= map.to &&
                map.skip !== undefined
              ) {
                const nextLevel =
                  map.skip === 'previous'
                    ? levelIndex[levelIndex.indexOf(level) - 1]
                    : levelIndex[levelIndex.indexOf(level) + 1]

                if (
                  map.skip === 'next' &&
                  nextLevel &&
                  parseInt(nextLevel, 10) <= parseInt(bounds.target, 10)
                ) {
                  level = nextLevel
                  currentQuestionId = 0
                } else if (
                  map.skip === 'previous' &&
                  nextLevel &&
                  parseInt(level, 10) < parseInt(bounds.target, 10)
                ) {
                  level = nextLevel
                  currentQuestionId = 0
                } else {
                  skipCategory = true
                }
              } else if (
                scorePercentage >= map.from &&
                scorePercentage <= map.to
              ) {
                skipCategory = true
              }
            })
          }
        }
      }
    }

    const questionGroup = dpQuestionCategories[category][level]
    let nextQuestionId = questionGroup[0]
    if (currentQuestionId !== 0) {
      nextQuestionId = questionGroup[questionGroup.indexOf(currentQuestionId) + 1]
    }

    if (nextQuestionId === undefined || skipCategory) {
      const nextCategory = getNextCourseCategory(category, scoreMap)
      if (nextCategory === null) return { nextQuestionId: 'finish' }
      category = nextCategory
      currentQuestionId = 0
      continue
    }

    const currentQuestion = dpQuestions[nextQuestionId]
    if (
      answers !== null &&
      currentQuestion.questionIdLink !== undefined &&
      answers[category] !== undefined
    ) {
      const answeredQuestions = Object.keys(answers[category])
      if (answeredQuestions.indexOf(currentQuestion.questionIdLink) > -1) {
        currentQuestionId = nextQuestionId
        continue
      }
    }

    return { nextQuestionId, category, level }
  }
}

/**
 * Gets the first DP question for a course-level quiz.
 */
export function dpFirstCourseLevelQuestionId(selectedCourseProfile) {
  return dpNextCourseLevelQuestionId(
    selectedCourseProfile,
    0,
    '1_1',
    []
  ).nextQuestionId
}

/**
 * Builds final DP scores for course-required categories only.
 */
export function dpBuildCourseLevelScore(allAnswers, selectedCourseProfile) {
  const scoreMap = getCourseLevelScoreMap(selectedCourseProfile)
  const formatAnswer = dpFormatAnswer(allAnswers || {}, {})
  const finalScore = []

  Object.keys(dpQuestionCategories).forEach(category => {
    const bounds = getCourseLevelBounds(scoreMap[category])
    if (!bounds) return

    let level = bounds.start
    let scoreComputed = false
    const levelIndex = Object.keys(dpPercentageComputation)
    let guard = 0

    while (!scoreComputed && guard <= levelIndex.length) {
      guard += 1

      if (
        typeof formatAnswer[category] === 'undefined' ||
        typeof formatAnswer[category][level] === 'undefined'
      ) {
        finalScore.push({
          category,
          score: 0,
          targetScore: scoreMap[category]
        })
        scoreComputed = true
        continue
      }

      const percentage = formatAnswer[category][level].percentage
      const nextQuestionMap = dpPercentageComputation[bounds.start][level]

      nextQuestionMap.forEach(range => {
        if (
          scoreComputed ||
          percentage < range.from ||
          percentage > range.to
        ) {
          return
        }

        if (range.skip !== undefined) {
          const nextLevel =
            range.skip === 'previous'
              ? levelIndex[levelIndex.indexOf(level) - 1]
              : levelIndex[levelIndex.indexOf(level) + 1]

          if (
            nextLevel &&
            formatAnswer[category][nextLevel] &&
            (
              (
                range.skip === 'previous' &&
                parseInt(level, 10) < parseInt(bounds.target, 10)
              ) ||
              (
                range.skip === 'next' &&
                parseInt(nextLevel, 10) <= parseInt(bounds.target, 10)
              )
            )
          ) {
            level = nextLevel
            return
          }

          finalScore.push({
            category,
            score: getCourseLevelFallbackScore(level, range.skip),
            targetScore: scoreMap[category]
          })
          scoreComputed = true
          return
        }

        finalScore.push({
          category,
          score: range.score,
          targetScore: scoreMap[category]
        })
        scoreComputed = true
      })
    }
  })

  return finalScore
}

/**
 * Digital Proficiency build the score
 */
export function dpBuildScore(allAnswers, dpPrescreeningResult) {
  const formatAnswer = dpFormatAnswer(allAnswers, dpPrescreeningResult)

  let finalScore = []
  Object.keys(dpQuestionCategories).forEach(categ => {
    // get prescreening level per category
    const baseLevel = getCurrentLevel(dpPrescreeningResult, categ)
    // use the base level to calculate the score
    let lvl = baseLevel;
    let scoreComputed = false
    const lvlIndex = Object.keys(dpPercentageComputation)

    while(!scoreComputed) {
      // if level questions are linked to the previous level (category 5.4)
      if (typeof formatAnswer[categ][lvl] === 'undefined') {
        lvl = lvlIndex[lvlIndex.indexOf(lvl.toString()) + 1]
        continue
      }

      const percentage = formatAnswer[categ][lvl].percentage
      // eslint-disable-next-line no-loop-func
      dpPercentageComputation[baseLevel][lvl].forEach(range => {
        if (percentage >= range.from && percentage <= range.to) {
          if (range.skip !== undefined) {
            if (range.skip === 'previous') {
              // 1 level down
              lvl = lvlIndex[lvlIndex.indexOf(lvl.toString()) - 1]
            } else {
              // 1 level up
              lvl = lvlIndex[lvlIndex.indexOf(lvl.toString()) + 1]
            }
          } else {
            finalScore.push({
              category: categ,
              score: range.score
            })
            scoreComputed = true
          }
        }
      })
    }
  });
  return finalScore;
}

/**
 * Digital Profiler format the answer
 */
function dpFormatAnswer(allAnswers, dpPrescreeningResult) {
  let formatAnswer = {}
  Object.keys(allAnswers).forEach((category) => {
    const categoryAnswer = allAnswers[category]
    Object.values(categoryAnswer).forEach((answer) => {

      const correct = answer.correct
      const level = answer.level

      if (typeof formatAnswer[category] === 'undefined') {
        formatAnswer[category] = {}
      }

      if (typeof formatAnswer[category][level] === 'undefined') {
        formatAnswer[category][level] = {
          'correct': 0,
          'totalQuestions': 0,
          'percentage': 0
        };
      }
      formatAnswer[category][level].totalQuestions ++;
      if (correct === true) {
        formatAnswer[category][level].correct ++;
      }
      formatAnswer[category][level].percentage = (formatAnswer[category][level].correct / formatAnswer[category][level].totalQuestions) * 100
    })
  })
  return formatAnswer
}

/**
 * Sends a learner form submission to the server.
 * @memberof learner/lib
 */
export function submitDpConfirmReset(invite: string) {
  return new Promise((resolve, reject) => {
    socket.emit('learner:dp-reset-answers', { invite }, (err, response) => {
      if (err) {
        return reject(err)
      }
      resolve(response)
    })
  })
}



// WEBPACK FOOTER //
// ./src/state/learner/lib.js